import { api } from "@/integrations/clients/carAuction/carAuctionClient";

export interface FeedbackListItem {
  id: string;
  title: string;
  description: string;
  stars: number;
  read: boolean;
  createdAt: string;
  imageCount: number;
  submittedBy?: { id: string; email?: string };
}

export interface FeedbackListResponse {
  items: FeedbackListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasUnread: boolean;
  averageStars?: number;
}

export interface FeedbackDetailMedia {
  id: string;
  originalName: string;
  previewUrl: string;
  fullSizeUrl: string;
}

export interface FeedbackDetail {
  id: string;
  title: string;
  description: string;
  stars: number;
  read: boolean;
  createdAt: string;
  submittedBy?: { id: string; email?: string };
  media: FeedbackDetailMedia[];
}

export type BugReportStatus = "NEW" | "ONGOING" | "SOLVED" | "REJECTED";

export interface BugReportListItem {
  id: string;
  title: string;
  description: string;
  category: string;
  status: BugReportStatus;
  read: boolean;
  createdAt: string;
  deviceType?: string;
  os?: string;
  imageCount: number;
  submittedBy?: { id: string; email?: string };
}

export interface BugReportListResponse {
  items: BugReportListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasUnread: boolean;
}

export interface BugReportComment {
  id: string;
  text: string;
  createdAt: string;
  /** Author: displayName is username or personalFirstName + personalLastName */
  admin?: { id: string; email?: string; displayName?: string };
}

export interface BugReportDetailMedia {
  id: string;
  originalName: string;
  previewUrl: string;
  fullSizeUrl: string;
}

export interface BugReportDetail {
  id: string;
  title: string;
  description: string;
  category: string;
  occurredAt: string;
  deviceType: string;
  os: string;
  status: BugReportStatus;
  read: boolean;
  createdAt: string;
  submittedBy?: { id: string; email?: string };
  media: BugReportDetailMedia[];
  comments: BugReportComment[];
}

export class AdminFeedbackService {
  static async getFeedbackList(
    page: number,
    limit: number
  ): Promise<FeedbackListResponse> {
    const response = await api.get<{
      success: boolean;
      data: FeedbackListResponse;
    }>(`/admin/feedback?page=${page}&limit=${limit}`);
    if (!response.success || !response.data) {
      throw new Error("Failed to fetch feedback list");
    }
    return response.data;
  }

  static async getFeedbackHasUnread(): Promise<boolean> {
    const response = await api.get<{
      success: boolean;
      data: { hasUnread: boolean };
    }>("/admin/feedback/has-unread");
    if (!response.success || !response.data) {
      return false;
    }
    return response.data.hasUnread;
  }

  static async getFeedbackById(id: string): Promise<FeedbackDetail> {
    const response = await api.get<{
      success: boolean;
      data: FeedbackDetail;
    }>(`/admin/feedback/${id}`);
    if (!response.success || !response.data) {
      throw new Error("Failed to fetch feedback");
    }
    return response.data;
  }

  static async getBugReportList(
    page: number,
    limit: number,
    status?: BugReportStatus
  ): Promise<BugReportListResponse> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.set("status", status);
    const response = await api.get<{
      success: boolean;
      data: BugReportListResponse;
    }>(`/admin/bug-reports?${params.toString()}`);
    if (!response.success || !response.data) {
      throw new Error("Failed to fetch bug report list");
    }
    return response.data;
  }

  static async getBugReportHasUnread(): Promise<boolean> {
    const response = await api.get<{
      success: boolean;
      data: { hasUnread: boolean };
    }>("/admin/bug-reports/has-unread");
    if (!response.success || !response.data) {
      return false;
    }
    return response.data.hasUnread;
  }

  static async getBugReportById(id: string): Promise<BugReportDetail> {
    const response = await api.get<{
      success: boolean;
      data: BugReportDetail;
    }>(`/admin/bug-reports/${id}`);
    if (!response.success || !response.data) {
      throw new Error("Failed to fetch bug report");
    }
    return response.data;
  }

  static async updateBugReportStatus(
    id: string,
    status: BugReportStatus
  ): Promise<void> {
    await api.patch(`/admin/bug-reports/${id}/status`, { status });
  }

  static async addBugReportComment(
    id: string,
    text: string,
    mentions?: string[]
  ): Promise<{ id: string; text: string; createdAt: string }> {
    const response = await api.post<{
      success: boolean;
      data: { id: string; text: string; createdAt: string };
    }>(`/admin/bug-reports/${id}/comments`, { text, mentions: mentions ?? [] });
    if (!response.success || !response.data) {
      throw new Error("Failed to add comment");
    }
    return response.data;
  }
}
